// PRD.300 — Visit Note User Preferences and Clinic Defaults
//
// Resolution hierarchy (PRD §5.10):
//
//   feature/module/RBAC availability → clinic default → user override → UI
//
// The clinic default is DERIVED, never stored per user: a user who has never
// customized Visit Notes has no `user_ui_preferences` row at all (PRD §6.4), so
// a later change to the clinic's type or module configuration flows straight
// through to them. "Reset to Default" therefore deletes the row rather than
// writing a frozen copy of the current default (PRD §5.8).
//
// This module is intentionally self-contained apart from intra-package imports
// (no workspace-package imports) so it runs under the node:test harness. The
// tab CATALOGUE (keys + display labels) is supplied by the caller, keeping UI
// labels out of data-access.

import {
  buildClinicWorkspacePreset,
  type ClinicWorkspacePresetInput,
  type VisitNoteTabKey,
} from './clinic-workspace-preset.js';
import type { PrimaryClinicType } from './clinic-classification.js';

// ─── Types ───

export type VisitNoteLayoutStyle = 'tabs' | 'accordion' | 'guided_note';
export type VisitNoteAccordionMode = 'single' | 'multiple';
export type GuidedNotePreset = 'general' | 'dental' | 'aesthetic' | 'custom';

export interface VisitNoteTabPref {
  key: string;
  label: string;
  visible: boolean;
}

export interface VisitNoteLayoutPref {
  style: VisitNoteLayoutStyle;
  accordionMode: VisitNoteAccordionMode;
  /** Tab key, or `__all__` for "open every section". */
  defaultOpenSection: string | null;
  rememberLastOpen: boolean;
  guidedNotePreset: GuidedNotePreset;
}

/**
 * The full Visit Note preference payload — the value stored under
 * `user_ui_preferences.value_json` for `preference_key = 'visit_note'`, and the
 * same shape the clinic default is derived into so the two can be merged.
 */
export interface VisitNotePreference {
  version: 1;
  layout: VisitNoteLayoutPref;
  /** Role preset id (`front_desk` | `doctor` | `nurse` | `billing`) or null. */
  rolePreset: string | null;
  /** Sub-module visibility for the Tabs / Accordion layouts. */
  tabs: VisitNoteTabPref[];
  /**
   * Guided layout keeps a SEPARATE sub-module set per discipline, so toggling
   * under Dental never bleeds into Aesthetic or into the shared `tabs`.
   */
  guidedTabsByPreset: Partial<Record<GuidedNotePreset, VisitNoteTabPref[]>>;
}

/** The `user_ui_preferences` PowerSync row shape. */
export interface UserUiPreferenceRow {
  id: string;
  org_id: string | null;
  user_id: string | null;
  preference_key: string | null;
  value_json: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Preference key for the Visit Note workspace configuration. */
export const VISIT_NOTE_PREFERENCE_KEY = 'visit_note';

// ─── Clinic type → Guided preset ───

/**
 * Map the clinic's primary type to its Guided Note preset (PRD.282, PRD.300
 * §5.3). The primary clinic type IS the selector for which guided note a clinic
 * gets — there is no separate clinic layout setting.
 *
 * Single source of truth: `guided-note-presets.ts` in web-admin re-exports this
 * rather than keeping a parallel mapping.
 */
export function guidedPresetForClinicType(primary: PrimaryClinicType): GuidedNotePreset {
  switch (primary) {
    case 'dental':
      return 'dental';
    case 'dermatology_aesthetic':
      return 'aesthetic';
    case 'general':
    case 'other':
    default:
      return 'general';
  }
}

// ─── Guided-preset default-on sub-modules (PRD.310) ───

/**
 * Sub-modules a specific Guided Note discipline enables BY DEFAULT, on top of
 * whatever the shared clinic-type workspace preset says.
 *
 * This exists so a generic sub-module can be default-on for one discipline
 * without becoming a clinic-level Patient Workspace toggle (which would mean a
 * new `patient_workspace_settings` column per section) and without being turned
 * on for every clinic type. It is ADDITIVE ONLY — an entry can switch a section
 * on, never off — so listing a key here can never subtract from another preset.
 *
 * It is a DEFAULT, not a lock: a saved user override still wins (PRD.300 §5.8),
 * and "Reset to Default" restores exactly this.
 */
export const GUIDED_PRESET_DEFAULT_ON: Partial<Record<GuidedNotePreset, readonly string[]>> = {
  // PRD.310 §9 — Dental Guided Notes open with Chief Complaint → Medical
  // History → Charting & Images. Other disciplines keep Medical History
  // available but off until the user enables it.
  // PRD.314 §7 — Medical History is universal, so it is now default-ON for
  // General and Aesthetic as well (PRD.310 had only Dental). Each discipline
  // then adds its own specialty history. General deliberately gets none: there
  // is no invented "General History".
  general: ['medical_history'],
  // PRD.316 — Dental opens with Clinical Findings on; other presets keep it
  // available but off until the user enables it.
  // PRD.318 — Dental opens with the dental treatment workflow. The generic
  // `treatment_plan` is deliberately NOT also enabled, so no clinic ends up
  // with two treatment sections purely from defaults.
  dental: [
    'medical_history',
    'dental_history',
    'clinical_findings',
    'investigations',
    'dental_treatment_plan',
  ],
  aesthetic: ['medical_history', 'derma_history'],
};

// ─── Clinic-derived default ───

/**
 * Layout defaults for a clinic with no user override. Guided is the default
 * layout (PRD §5.2); the clinic's primary type selects which guided preset.
 */
export function clinicDefaultLayout(primary: PrimaryClinicType): VisitNoteLayoutPref {
  return {
    style: 'guided_note',
    accordionMode: 'single',
    defaultOpenSection: 'summary',
    rememberLastOpen: false,
    guidedNotePreset: guidedPresetForClinicType(primary),
  };
}

/**
 * Derive the complete clinic-default Visit Note configuration.
 *
 * Sub-module visibility comes from `buildClinicWorkspacePreset` — the SAME
 * clinic-type → tab mapping "Apply Clinic Defaults" uses in Patient Workspace
 * Settings — so no second mapping is introduced (PRD §7.1). Tabs the preset
 * doesn't speak to (chief_complaint, symptoms, diagnoses, pharmacy) keep the
 * catalogue's own default visibility.
 *
 * `catalogue` is the full ordered tab list with display labels, supplied by the
 * caller so UI strings stay out of data-access.
 */
export function buildClinicVisitNoteDefault(
  input: ClinicWorkspacePresetInput,
  catalogue: readonly VisitNoteTabPref[],
): VisitNotePreference {
  const preset = buildClinicWorkspacePreset(input);
  const guidedPreset = guidedPresetForClinicType(input.primaryClinicType);
  const guidedOn = GUIDED_PRESET_DEFAULT_ON[guidedPreset] ?? [];
  const tabs = catalogue.map((t) => {
    const configured = preset.visitNoteTabs[t.key as VisitNoteTabKey];
    const visible = typeof configured === 'boolean' ? configured : t.visible;
    // PRD.310 — a discipline may switch a generic sub-module on by default
    // without the shared workspace preset (and its clinic-level Patient
    // Workspace toggles) having to know about it. Additive only.
    return { ...t, visible: visible || guidedOn.includes(t.key) };
  });
  return {
    version: 1,
    layout: clinicDefaultLayout(input.primaryClinicType),
    rolePreset: null,
    tabs,
    // Empty: every discipline starts from the clinic-derived `tabs` above until
    // the user customizes that discipline specifically.
    guidedTabsByPreset: {},
  };
}

// ─── Parsing stored overrides ───

function isTabPref(v: unknown): v is VisitNoteTabPref {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return typeof o.key === 'string' && typeof o.visible === 'boolean';
}

function normalizeTabs(
  raw: unknown,
  catalogue: readonly VisitNoteTabPref[],
  /**
   * PRD.310 — visibility source for sub-modules ABSENT from the stored payload
   * (i.e. added since the user saved). The user never expressed an intent about
   * those, so they follow the CLINIC DEFAULT rather than the catalogue's bare
   * flag — otherwise a discipline default like Dental's Medical History would
   * reach only users who have never customized their Visit Note. Sub-modules
   * the payload does mention keep the user's stored choice, untouched.
   */
  newKeyDefaults?: readonly VisitNoteTabPref[],
): VisitNoteTabPref[] {
  const defaults = new Map((newKeyDefaults ?? []).map((t) => [t.key, t.visible]));
  // No usable stored list at all (absent, or a malformed payload): the user has
  // expressed nothing, so every key follows the clinic default.
  if (!Array.isArray(raw)) {
    return catalogue.map((t) => ({ ...t, visible: defaults.get(t.key) ?? t.visible }));
  }
  const known = new Map(catalogue.map((t) => [t.key, t]));
  const stored = raw.filter(isTabPref).filter((t) => known.has(t.key));
  // Labels always come from the catalogue so a rename ships without a rewrite.
  const result = stored.map((t) => ({ ...known.get(t.key)!, visible: t.visible }));
  // Append sub-modules added since the preference was saved, at their catalogue
  // position, so new features appear for existing users.
  const seen = new Set(result.map((t) => t.key));
  catalogue.forEach((d, i) => {
    if (seen.has(d.key)) return;
    let insertAt = 0;
    for (let j = i - 1; j >= 0; j--) {
      const idx = result.findIndex((t) => t.key === catalogue[j].key);
      if (idx >= 0) {
        insertAt = idx + 1;
        break;
      }
    }
    result.splice(insertAt, 0, { ...d, visible: defaults.get(d.key) ?? d.visible });
    seen.add(d.key);
  });
  return result;
}

const LAYOUT_STYLES: readonly VisitNoteLayoutStyle[] = ['tabs', 'accordion', 'guided_note'];
const GUIDED_PRESETS: readonly GuidedNotePreset[] = ['general', 'dental', 'aesthetic', 'custom'];

/**
 * Parse an untrusted stored preference into a canonical one, falling back to
 * `fallback` field-by-field. Never throws: a malformed or half-written value
 * degrades to the clinic default rather than blanking the Visit Note.
 */
export function parseVisitNotePreference(
  raw: unknown,
  fallback: VisitNotePreference,
  catalogue: readonly VisitNoteTabPref[],
): VisitNotePreference {
  let obj: Record<string, unknown> = {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        obj = parsed as Record<string, unknown>;
      }
    } catch {
      return fallback;
    }
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    obj = raw as Record<string, unknown>;
  } else {
    return fallback;
  }

  const layoutRaw = (obj.layout ?? {}) as Record<string, unknown>;
  const style = LAYOUT_STYLES.includes(layoutRaw.style as VisitNoteLayoutStyle)
    ? (layoutRaw.style as VisitNoteLayoutStyle)
    : fallback.layout.style;
  const guidedNotePreset = GUIDED_PRESETS.includes(layoutRaw.guidedNotePreset as GuidedNotePreset)
    ? (layoutRaw.guidedNotePreset as GuidedNotePreset)
    : fallback.layout.guidedNotePreset;

  const guidedTabsByPreset: Partial<Record<GuidedNotePreset, VisitNoteTabPref[]>> = {};
  const guidedRaw = obj.guidedTabsByPreset;
  if (guidedRaw && typeof guidedRaw === 'object' && !Array.isArray(guidedRaw)) {
    for (const preset of GUIDED_PRESETS) {
      const entry = (guidedRaw as Record<string, unknown>)[preset];
      if (Array.isArray(entry))
        guidedTabsByPreset[preset] = normalizeTabs(entry, catalogue, fallback.tabs);
    }
  }

  return {
    version: 1,
    layout: {
      style,
      accordionMode: layoutRaw.accordionMode === 'multiple' ? 'multiple' : 'single',
      defaultOpenSection:
        typeof layoutRaw.defaultOpenSection === 'string'
          ? layoutRaw.defaultOpenSection
          : layoutRaw.defaultOpenSection === null
            ? null
            : fallback.layout.defaultOpenSection,
      rememberLastOpen: layoutRaw.rememberLastOpen === true,
      guidedNotePreset,
    },
    rolePreset: typeof obj.rolePreset === 'string' ? obj.rolePreset : null,
    tabs: normalizeTabs(obj.tabs, catalogue, fallback.tabs),
    guidedTabsByPreset,
  };
}

// ─── Availability intersection ───

export interface VisitNoteAvailability {
  /**
   * Sub-module keys the clinic + feature flags + RBAC currently allow. `null`
   * means "not resolved yet" — the caller's own state hasn't loaded, so no
   * constraint is applied and the UI doesn't blank out mid-load.
   */
  allowedKeys: Set<string> | null;
}

/**
 * The effective Visit Note configuration.
 *
 * `override` is the user's saved preference or null. Availability is applied
 * LAST and is authoritative (PRD §5.9): a saved preference can only ever
 * subtract from what the clinic/RBAC allow, never add to it — so an old
 * preference naming a module the user has since lost access to stays hidden.
 */
export function resolveVisitNoteConfig(args: {
  clinicDefault: VisitNotePreference;
  override: VisitNotePreference | null;
  availability: VisitNoteAvailability;
}): {
  layout: VisitNoteLayoutPref;
  rolePreset: string | null;
  /** Visible sub-modules for the Tabs / Accordion layouts. */
  visibleTabs: VisitNoteTabPref[];
  /** Visible sub-modules for the active Guided preset. */
  visibleGuidedTabs: VisitNoteTabPref[];
  /** True when the user is following the clinic default (no saved override). */
  isDefault: boolean;
} {
  const { clinicDefault, override, availability } = args;
  const effective = override ?? clinicDefault;
  const allowed = availability.allowedKeys;
  const permit = (tabs: VisitNoteTabPref[]) =>
    tabs.filter((t) => t.visible && (!allowed || allowed.has(t.key)));

  // Guided: the active discipline's own set when the user has customized it,
  // otherwise the shared set — which for a default user IS the clinic-derived
  // module selection.
  const guidedSource =
    effective.guidedTabsByPreset[effective.layout.guidedNotePreset] ?? effective.tabs;

  return {
    layout: effective.layout,
    rolePreset: effective.rolePreset,
    visibleTabs: permit(effective.tabs),
    visibleGuidedTabs: permit(guidedSource),
    isDefault: override === null,
  };
}
